import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnInit,
    Output
} from '@angular/core';
import {
    ApiClientService,
    CachedValuesService,
    getTableSettingKey,
    SessionStorageKey,
    SessionStorageService
} from '@services/index';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ListTypeItem } from 'app/types/list-type-item.type';
import { Observable, map } from 'rxjs';
import { SelectedFilter, EMPTY_FILTER, FilterData, FilterConfig } from './filter-model';
import { simpleClone } from '@helpers/object-helper';
import { Counterpart, User } from 'app/types';
import { DateTime } from 'luxon';
import { Predicate, PredicateOperator } from 'app/enums';
import { GetCustomerResponse } from '@services/api-client/responses';
import { TranslateService } from '@ngx-translate/core';

export enum TableType {
    Agreements = 'agreements',
    Inventory = 'inventory',
    ListTypes = 'listtypes'
}

@UntilDestroy()
@Component({
    selector: 'app-agreement-filter',
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgreementFilterComponent implements OnInit {
    @Input() public config: FilterConfig = { fields: [] };
    @Input() public tableType: TableType;
    @HostBinding('class.visible') @Input() public visible = false;
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);

    private _storedFilter: SelectedFilter = simpleClone<SelectedFilter>(EMPTY_FILTER);
    public get storedFilter() {
        return this._storedFilter;
    }

    public set storedFilter(filter: SelectedFilter) {
        this._storedFilter = createValidFilter(filter);
    }
    public selectedFilter: SelectedFilter = simpleClone<SelectedFilter>(EMPTY_FILTER);
    public data: FilterData = simpleClone<FilterData>(EMPTY_FILTER);
    @Output() public filterChange: EventEmitter<SelectedFilter> = new EventEmitter<SelectedFilter>();
    private isEmpty = true;
    @Output() public isFilterEmptyChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    public readonly users$: Observable<MultiSelectItem[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName // + ' - ' + item.email
                } as MultiSelectItem;
            })
        )
    );

    public ngOnInit() {
        this.storedFilter = this.sessionStorageService.getObject<SelectedFilter>(
            getTableSettingKey(SessionStorageKey.TableFilter, this.tableType),
            deserializeDateRange
        );

        this.initializeFilters();
        this.emitIsFilterEmpty(this.storedFilter);
        this.updateStoredFilter();
    }

    public getPredicates() {
        const predicates: Predicate<boolean | string>[] = [];
        Object.entries(this.selectedFilter).forEach(([key, value]) => {
            //console.log(`Attribute: ${key}, Value: ${value}`);
            if ((typeof value === 'string' || typeof value === 'number') && !!value) {
                predicates.push({
                    propertyName: key,
                    operator: PredicateOperator.Contains,
                    value: value.toString()
                });
            } else if (typeof value === 'boolean' && !!value) {
                predicates.push({
                    propertyName: key,
                    operator: PredicateOperator.Equals,
                    value: value
                });
            } else if (Array.isArray(value)) {
                //console.log(`Array at key: ${key}`);
                // value.forEach((arrayValue, index) => {
                //     console.log(`  Index: ${index}, Value: ${arrayValue}`);
                // });
                if (value.length > 0) {
                    predicates.push({
                        propertyName: key,
                        operator: PredicateOperator.Contains,
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        value: `${value}`
                    });
                }
            } else {
                //console.log(`Attribute: ${key}, Value: ${value}`);
            }
        });

        return predicates;
    }

    private initializeFilters() {
        if (this.config.fields.includes('type')) {
            this.cachedValuesService.agreementTypes$
                .pipe(
                    map(
                        (data) =>
                            (this.data.type = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: this.translateService.instant(
                                        'FEATURES.AGREEMENTS.AGREEMENT_TYPES.' + item.name
                                    ) as string
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('categories')) {
            this.apiClientService
                .getTypeListItems$('CATEGORY')
                .pipe(
                    map(
                        (data) =>
                            (this.data.categories = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: item.value
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('areas')) {
            this.apiClientService
                .getTypeListItems$('AREA')
                .pipe(
                    map(
                        (data) =>
                            (this.data.areas = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: item.value
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('customers')) {
            this.cachedValuesService.customers$
                .pipe(
                    map(
                        (data) =>
                            (this.data.customers = data.map((item: GetCustomerResponse) => {
                                return {
                                    id: item.id,
                                    name: item.name
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('counterparts')) {
            this.cachedValuesService.counterparts$
                .pipe(
                    map(
                        (data) =>
                            (this.data.counterparts = data.map((item: Counterpart) => {
                                return {
                                    id: item.id,
                                    name: item.companyName
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('manufacturer')) {
            this.apiClientService
                .getTypeListItems$('MANUFACTURER')
                .pipe(
                    map(
                        (data) =>
                            (this.data.manufacturer = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: item.value
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('inventoryusers')) {
            this.apiClientService
                .getTypeListItems$('INVENTORY_USERS')
                .pipe(
                    map(
                        (data) =>
                            (this.data.inventoryusers = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: item.value
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('typeOfInventory')) {
            this.apiClientService
                .getTypeListItems$('TYPE_OF_INVENTORY')
                .pipe(
                    map(
                        (data) =>
                            (this.data.typeOfInventory = data.map((item: ListTypeItem) => {
                                return {
                                    id: item.name,
                                    name: item.name
                                } as MultiSelectItem;
                            }))
                    )
                )
                .subscribe();
        }
        if (this.config.fields.includes('listTypes')) {
            this.data.listTypes = this.cachedValuesService.getListTypeOptions().map(
                (item) =>
                    ({
                        id: item.value,
                        name: item.label
                    }) as MultiSelectItem
            );
        }
        this.data.status = this.cachedValuesService.getAgreementStatusOptions();
        this.data.endsInMonths = this.cachedValuesService.getEndsInMonthsOptions();

        this.selectedFilter = simpleClone<SelectedFilter>(this.storedFilter);
        this.filterChange.emit(this.selectedFilter);
    }

    public setFilterValue(key: string, value: number | string) {
        let parsedValue = value;
        if (key !== 'responsible' && key !== 'categories') {
            parsedValue = typeof value === 'string' ? parseInt(value, 10) : value;
        }

        if (Array.isArray(EMPTY_FILTER[key])) {
            const items: MultiSelectId[] = [];
            items.push(parsedValue);
            this.selectedFilter[key] = items;
        } else {
            this.selectedFilter[key] = parsedValue;
        }

        this.updateStoredFilter();
    }

    public onMultiSelectChange(key: string, items: MultiSelectId[]) {
        this.selectedFilter[key] = items;
        this.updateStoredFilter();
    }

    public onSelectChange(key: string, item: MultiSelectId) {
        this.selectedFilter[key] = item;
        this.updateStoredFilter();
    }

    public clearFilter() {
        this.selectedFilter = simpleClone<SelectedFilter>(EMPTY_FILTER);
        this.updateStoredFilter(true);
    }

    private updateStoredFilter(forceUpdate?: boolean) {
        if (this.isFilterChanged() || forceUpdate) {
            this.storedFilter = simpleClone<SelectedFilter>(this.selectedFilter);
            const filterToStore = this.isDefaultDateRangeSelected()
                ? { ...this.selectedFilter, dateRange: { to: null, from: null } }
                : this.selectedFilter;
            this.sessionStorageService.setObject(
                getTableSettingKey(SessionStorageKey.TableFilter, this.tableType),
                filterToStore
            );
            this.emitIsFilterEmpty(this.selectedFilter);
            this.filterChange.emit(this.selectedFilter);
        }
    }

    private isFilterChanged() {
        return Object.keys(this.selectedFilter).some((key) => {
            const filterKey = key as keyof typeof this.selectedFilter; // Assert key is a valid key of the object
            return JSON.stringify(this.selectedFilter[filterKey]) !== JSON.stringify(this.storedFilter[filterKey]);
        });
    }

    private emitIsFilterEmpty(filter: SelectedFilter) {
        if (this.isEmpty !== this.isFilterEmpty(filter)) {
            this.isEmpty = !this.isEmpty;
            this.isFilterEmptyChanged.emit(this.isEmpty);
        }
    }

    private isFilterEmpty(filter: SelectedFilter) {
        // if (this.config.fields.includes('dateRange')) {
        //     return JSON.stringify(filter) === JSON.stringify(EMPTY_FILTER);
        // }
        return JSON.stringify({ ...filter, dateRange: null }) === JSON.stringify({ ...EMPTY_FILTER, dateRange: null });
    }

    private getDefaultDateRange() {
        const today = DateTime.now().startOf('day');
        return { from: today.minus({ month: 3 }), to: today.endOf('day') };
    }

    private isDefaultDateRangeSelected() {
        const defaultDateRange = this.getDefaultDateRange();

        return (
            this.selectedFilter.dateRange.from != null &&
            defaultDateRange.from.equals(this.selectedFilter.dateRange.from) &&
            this.selectedFilter.dateRange.to != null &&
            defaultDateRange.to.equals(this.selectedFilter.dateRange.to)
        );
    }
}

export function deserializeDateRange(filter: SelectedFilter): SelectedFilter {
    const deserializeDateTime = (value: unknown) =>
        typeof value === 'string' ? DateTime.fromISO(value) : (value as DateTime);

    if (filter) {
        return {
            ...filter,
            dateRange: {
                from: deserializeDateTime(filter.dateRange.from),
                to: deserializeDateTime(filter.dateRange.to)
            }
        };
    }
    return simpleClone<SelectedFilter>(EMPTY_FILTER);
}

export function createValidFilter(filter: SelectedFilter) {
    const validFilter = simpleClone<SelectedFilter>(EMPTY_FILTER);

    if (filter) {
        if (!Object.keys(validFilter).every((key) => Object.keys(filter).includes(key))) {
            console.warn(
                'Missing keys:',
                Object.keys(validFilter)
                    .filter((key) => !Object.keys(filter).includes(key))
                    .join(', ')
            );
        }
        if (!Object.keys(filter).every((key) => Object.keys(validFilter).includes(key))) {
            console.warn(
                'Redundant keys:',
                Object.keys(filter)
                    .filter((key) => !Object.keys(validFilter).includes(key))
                    .join(', ')
            );
        }
        for (const key of Object.keys(validFilter).filter((key) => Array.isArray(validFilter[key]))) {
            validFilter[key] = (filter?.[key] || []) as MultiSelectId[];
        }
        for (const key of Object.keys(validFilter).filter(
            (key) => !Array.isArray(validFilter[key]) && key !== 'dateRange'
        )) {
            validFilter[key] = (filter?.[key] ?? validFilter[key]) as MultiSelectId;
        }
        validFilter.dateRange = filter?.dateRange || {
            from: null,
            to: null
        };
    }

    return validFilter;
}
